import React, { useState, useRef, useEffect } from "react";
// import { ChevronLeft, ChevronRight } from "lucide-react"; // Removed Lucide chevrons

import { INFRA_PAGE } from "../../data/constants";

const processes = INFRA_PAGE.process.processes;

const OurProcessSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const process = processes[activeIndex];

  const timelineRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // Mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.pageX - (timelineRef.current?.offsetLeft || 0);
    scrollLeft.current = timelineRef.current?.scrollLeft || 0;
    setPaused(true);
  };
  const handleMouseUpLeave = () => {
    isDragging.current = false;
    setPaused(false);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - (timelineRef.current?.offsetLeft || 0);
    const walk = (x - startX.current) * 1.5;
    if (timelineRef.current) {
      timelineRef.current.scrollLeft = scrollLeft.current - walk;
    }
  };

  // Touch events
  const handleTouchStart = (e: React.TouchEvent) => {
    isDragging.current = true;
    startX.current = e.touches[0].pageX - (timelineRef.current?.offsetLeft || 0);
    scrollLeft.current = timelineRef.current?.scrollLeft || 0;
    setPaused(true);
  };
  const handleTouchEnd = () => {
    isDragging.current = false;
    setPaused(false);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const x = e.touches[0].pageX - (timelineRef.current?.offsetLeft || 0);
    const walk = (x - startX.current) * 1.5;
    if (timelineRef.current) {
      timelineRef.current.scrollLeft = scrollLeft.current - walk;
    }
  };

  // Auto-advance with progress bar
  useEffect(() => {
    setProgress(0);
    const total = 8000; // Match TimelineSection speed
    const step = 30;
    let elapsed = 0;
    const iv = setInterval(() => {
      if (!paused) {
        elapsed += step;
        setProgress(Math.min((elapsed / total) * 100, 100));
        if (elapsed >= total) {
          setActiveIndex((i) => (i < processes.length - 1 ? i + 1 : 0));
          elapsed = 0;
          setProgress(0);
        }
      }
    }, step);
    return () => clearInterval(iv);
  }, [activeIndex, paused]);

  // Scroll current tab into view on mobile
  useEffect(() => {
    if (window.innerWidth >= 768) return;
    const buttons = timelineRef.current?.querySelectorAll("button");
    const activeButton = buttons?.[activeIndex] as HTMLElement;
    if (activeButton && timelineRef.current) {
      const container = timelineRef.current;
      const offsetLeft =
        activeButton.offsetLeft - container.offsetWidth / 2 + activeButton.offsetWidth / 2;

      container.scrollTo({
        left: offsetLeft,
        behavior: "smooth",
      });
    }
  }, [activeIndex]);

  return (
    <section className="bg-[#F5F7FA] py-16 px-6 md:px-20">
      {/* Title & Controls */}
      <div className="flex items-center justify-between mb-10">
        <h2 className="font-utility font-medium text-[30px] md:text-[54px] leading-[120%] text-[#011621]">
          {INFRA_PAGE.process.title}
        </h2>
        

      </div>

      {/* Process Content */}
      <div className="flex flex-col md:flex-row items-start mb-10 gap-8">
        <img
          src={process.image}
          alt={process.name}
          className="w-full max-w-[515px] h-[353px] object-cover"
        />
        <div className="ml-0 md:ml-[32px] max-w-[514px]">
          <h3 className="font-utility font-medium text-[30px] md:text-[36px] text-[#011621]">
            {process.name}
          </h3>
          <p className="mt-2 font-inter text-[18px] md:text-[20px] leading-[150%] text-[#033652]">
            <span className="font-normal text-[#4F7286]">Machinery:</span>{" "}
            <span className="font-normal text-[#033652]">{process.machinery}</span>
          </p>
          <p className="mt-2 font-inter text-[18px] md:text-[20px] leading-[150%] text-[#033652]">
            <span className="font-normal text-[#4F7286]">Description:</span>{" "}
            <span className="font-normal text-[#033652]">{process.description}</span>
          </p>
          <div className="mt-4 font-inter text-[18px] md:text-[20px] leading-[150%] text-[#033652]">
            <p>
              <span className="font-normal text-[#4F7286]">Minimum size:</span>{" "}
              {process.minSize || "-"}
            </p>
            <p>
              <span className="font-normal text-[#4F7286]">Maximum size:</span>{" "}
              {process.maxSize || "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Timeline Tabs */}
      <div
        ref={timelineRef}
        className="flex gap-[75px] overflow-x-auto py-6 px-1 pr-6 select-none cursor-grab no-scrollbar"
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseUpLeave}
        onMouseUp={handleMouseUpLeave}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onTouchCancel={() => setPaused(false)}
      >
        {processes.map((p, idx) => {
          const isActive = idx === activeIndex;
          return (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={`flex flex-col items-center w-fit transition-all duration-150 whitespace-nowrap ${
                isActive ? "scale-110" : "opacity-70 hover:opacity-100"
              }`}
            >
              <div className="inline-flex flex-col items-center w-fit">
                <span
                  className={`text-[14px] md:text-[18px] truncate ${
                    isActive ? "text-[#033652] font-semibold" : "text-[#033652]"
                  }`}
                >
                  {p.name}
                </span>
                <span className="mt-4 h-[4px] bg-[#B3C3CB] relative overflow-hidden  " style={{ width: "calc(100% + 20px)" }}>
                  {isActive && (
                    <span
                      className="absolute left-0 top-0 h-full bg-[#033652] transition-all duration-[50ms]"
                      style={{ width: `${progress}%` }}
                    />
                  )}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};


export default OurProcessSection;
